import React, { useCallback, useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Search, Star, StarOff, PowerOff, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminListings() {
    const [q, setQ] = useState("");
    const [city, setCity] = useState("");
    const [type, setType] = useState("");
    const [isActive, setIsActive] = useState("");
    const [minRent, setMinRent] = useState("");
    const [maxRent, setMaxRent] = useState("");
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page: 1, limit: 100 };
            if (q) params.q = q;
            if (city) params.city = city;
            if (type) params.listing_type = type;
            if (isActive !== "") params.is_active = isActive === "true";
            if (minRent) params.min_rent = parseInt(minRent);
            if (maxRent) params.max_rent = parseInt(maxRent);
            const { data } = await adminApi.get("/admin/listings", { params });
            setRows(data.listings || []);
            setTotal(data.total || 0);
        } catch (e) { toast.error("Couldn't load listings"); }
        finally { setLoading(false); }
    }, [q, city, type, isActive, minRent, maxRent]);
    useEffect(() => { load(); }, [load]);

    const act = async (id, action) => {
        try {
            await adminApi.post(`/admin/listings/${id}/action`, { action });
            toast.success(action);
            load();
        } catch (e) { toast.error("Failed"); }
    };

    return (
        <div data-testid="admin-listings-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-black">Listings</h1>
                    <p className="text-sm text-white/50">{total} total</p>
                </div>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-3 flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input placeholder="Search title/description/address…" value={q} onChange={(e) => setQ(e.target.value)}
                        className="w-full h-9 pl-9 pr-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none focus:border-[#E85D5D]" />
                </div>
                <input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)}
                    className="h-9 w-28 px-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none" />
                <select value={type} onChange={(e) => setType(e.target.value)}
                    className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none">
                    <option value="">All types</option>
                    <option value="flat">Flat</option>
                    <option value="room">Room</option>
                </select>
                <select value={isActive} onChange={(e) => setIsActive(e.target.value)}
                    className="h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none">
                    <option value="">Any status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                </select>
                <input placeholder="Min ₹" value={minRent} onChange={(e) => setMinRent(e.target.value)} type="number"
                    className="h-9 w-24 px-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none" />
                <input placeholder="Max ₹" value={maxRent} onChange={(e) => setMaxRent(e.target.value)} type="number"
                    className="h-9 w-24 px-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none" />
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] font-black uppercase tracking-widest text-white/50 border-b border-white/10">
                                <th className="px-4 py-3">Title</th>
                                <th className="px-4 py-3">Owner</th>
                                <th className="px-4 py-3">City</th>
                                <th className="px-4 py-3">Rent</th>
                                <th className="px-4 py-3">Type</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3"></th>
                            </tr>
                        </thead>
                        <tbody data-testid="admin-listings-table">
                            {loading ? (
                                <tr><td colSpan={7} className="text-center text-white/50 py-8">Loading…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={7} className="text-center text-white/50 py-8">No listings match</td></tr>
                            ) : rows.map((l) => (
                                <tr key={l.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-listing-row-${l.id}`}>
                                    <td className="px-4 py-3 font-semibold min-w-[160px] truncate">{l.title || "—"}{l.is_featured && <Star className="inline-block h-3 w-3 ml-1 text-[#F59E0B]" fill="currentColor" strokeWidth={0} />}</td>
                                    <td className="px-4 py-3 text-white/70">{l.owner?.name || l.owner?.email || "—"}</td>
                                    <td className="px-4 py-3 text-white/70">{l.city || "—"}</td>
                                    <td className="px-4 py-3 text-white/70">₹{(l.rent || 0).toLocaleString()}</td>
                                    <td className="px-4 py-3 text-white/70">{l.type || "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[9px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 ${l.is_active ? "bg-[#10B981]/20 text-[#10B981]" : "bg-white/10 text-white/50"}`}>
                                            {l.is_active ? "active" : "inactive"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right whitespace-nowrap">
                                        <button onClick={() => act(l.id, l.is_featured ? "unfeature" : "feature")} data-testid={`admin-listing-feature-${l.id}`}
                                            className="h-8 w-8 rounded-lg hover:bg-white/10 grid place-items-center" title="Feature">
                                            {l.is_featured ? <StarOff className="h-4 w-4 text-[#F59E0B]" /> : <Star className="h-4 w-4 text-white/60" />}
                                        </button>
                                        <button onClick={() => act(l.id, l.is_active ? "deactivate" : "activate")} data-testid={`admin-listing-toggle-${l.id}`}
                                            className="h-8 w-8 rounded-lg hover:bg-white/10 grid place-items-center" title={l.is_active ? "Deactivate" : "Activate"}>
                                            {l.is_active ? <PowerOff className="h-4 w-4 text-white/60" /> : <Power className="h-4 w-4 text-[#10B981]" />}
                                        </button>
                                        <button onClick={() => { if (window.confirm("Delete listing?")) act(l.id, "delete"); }} data-testid={`admin-listing-delete-${l.id}`}
                                            className="h-8 w-8 rounded-lg hover:bg-white/10 grid place-items-center" title="Delete">
                                            <Trash2 className="h-4 w-4 text-[#EF4444]" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
